import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OrderService } from '../shared/core/state/order/order.service';
import { PageLoaderService } from '../shared/util/pageloader.service';
import { Order, OrderStatus } from '../shared/core/state/order/order.model';
import { finalize, map } from 'rxjs';

@Component({
  selector: 'black-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.css'],
  standalone: false,
})
export class OrderComponent implements OnInit {
  private orderService = inject(OrderService);
  private pageLoaderService = inject(PageLoaderService);
  private router = inject(Router);

  orders: Order[] = [];
  isLoading = true;
  OrderStatus = OrderStatus;

  get hasAwaitingApprovalOrders(): boolean {
    return this.orders.some((o) => o.status === OrderStatus.AWAITING_APPROVAL);
  }

  ngOnInit(): void {
    this.pageLoaderService.open();
    this.orderService
      .getAll(1, 50, undefined, true)
      .pipe(
        map((orderResponse) => orderResponse.docs ?? []),
        finalize(() => {
          this.isLoading = false;
          this.pageLoaderService.close();
        })
      )
      .subscribe({
        next: (orders) => {
          this.orders = orders;
        },
        error: () => {
          this.orders = [];
        },
      });
  }

  getStatusBadge(status?: OrderStatus): { label: string; badgeClass: string; icon: string } {
    switch (status) {
      case OrderStatus.AWAITING_APPROVAL:
        return { label: 'Verification Pending', badgeClass: 'daisy-badge-warning text-warning-content', icon: 'fa-clock' };
      case OrderStatus.PARTIALLY_PAID:
        return { label: 'Partially Paid', badgeClass: 'daisy-badge-primary', icon: 'fa-circle-half-stroke' };
      case OrderStatus.OVERDUE:
        return { label: 'Payment Overdue', badgeClass: 'daisy-badge-error text-white', icon: 'fa-triangle-exclamation' };
      case OrderStatus.COMPLETED:
        return { label: 'Completed', badgeClass: 'daisy-badge-success text-white', icon: 'fa-circle-check' };
      case OrderStatus.PENDING:
        return { label: 'Payment Pending', badgeClass: 'daisy-badge-warning daisy-badge-outline', icon: 'fa-hourglass-half' };
      case OrderStatus.CANCELLED:
        return { label: 'Cancelled', badgeClass: 'daisy-badge-ghost', icon: 'fa-ban' };
      case OrderStatus.REFUNDED:
        return { label: 'Refunded', badgeClass: 'daisy-badge-neutral', icon: 'fa-rotate-left' };
      default:
        return { label: status || 'Unknown', badgeClass: 'daisy-badge-ghost', icon: 'fa-info' };
    }
  }

  payOrder(orderId?: string): void {
    if (orderId) {
      this.router.navigate(['/order/payment'], { queryParams: { orderId } });
    }
  }

  viewCourse(order: Order): void {
    const courseId = order.items?.[0]?._id || order.items?.[0];
    this.router.navigate(courseId ? ['/courses', courseId, 'playlist'] : ['/dashboard/my-courses']);
  }

  formatDate(timestamp?: string | Date): string {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
}
